import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getUserMentions } from "../../api/api";
import { defaultPfpUrl, formatRelativeTimestamp } from "../utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  NotificationTabHeader,
  NotificationEmptyState,
  NotificationErrorPanel,
  NotificationRequestCard,
} from "./NotificationTabShared";

/**
 * @param {string} sourceType
 * @returns {string}
 */
function mentionSourceLabel(sourceType) {
  if (sourceType === "comment") {
    return "a comment";
  }
  if (sourceType === "message") {
    return "a message";
  }
  if (sourceType === "event") {
    return "an event";
  }
  return "a post";
}

/**
 * Inbox of places the current account was @mentioned.
 *
 * @param {Object} props
 * @param {number} props.userId
 * @returns {JSX.Element}
 */
function MentionsTab({ userId }) {
  const [mentions, setMentions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setMentions([]);
      return undefined;
    }
    setLoading(true);
    setError("");
    (async () => {
      const result = await getUserMentions(userId);
      if (cancelled) {
        return;
      }
      if (!result?.success) {
        setError(result?.error || "Failed to load mentions");
        setMentions([]);
      } else {
        setMentions(result.data || []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return (
    <div>
      <NotificationTabHeader
        title="Mentions"
        description="See where you have been mentioned."
      />
      {error ? <NotificationErrorPanel message={error} /> : null}
      {!error && !loading && mentions.length === 0 ? (
        <NotificationEmptyState
          title="No mentions yet"
          subtitle="When someone tags you with @, it will show up here."
          className="border-solid bg-ui-card"
        />
      ) : null}
      {mentions.length > 0 ? (
        <div className="flex flex-col gap-3">
          {mentions.map((mention) => {
            const time = formatRelativeTimestamp(mention.created_at);
            return (
              <NotificationRequestCard key={mention.id}>
                <Link
                  to={mention.path || "/notifications/mentions"}
                  className="flex min-w-0 items-start gap-3"
                >
                  <Avatar className="h-10 w-10 shrink-0">
                    <AvatarImage
                      src={mention.author_pfp || defaultPfpUrl}
                      alt=""
                    />
                    <AvatarFallback>
                      {(mention.author_name || "?").slice(0, 1).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-text-primary">
                      <span className="font-semibold">
                        {mention.author_name || "Someone"}
                      </span>{" "}
                      mentioned you in {mentionSourceLabel(mention.source_type)}
                      {time ? (
                        <span className="text-text-muted"> · {time}</span>
                      ) : null}
                    </p>
                    {mention.preview ? (
                      <p className="mt-1 truncate text-sm text-text-secondary">
                        {mention.preview}
                      </p>
                    ) : null}
                  </div>
                </Link>
              </NotificationRequestCard>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default MentionsTab;
